/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link } from '@coursera/cds-core';

import type { Namespaces } from 'bundles/epic/__generated__/namespaces';
import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';

import _t from 'i18n!nls/enroll-course';

type StandaloneCourseEnrollmentModalCopyUpdatesValues =
  Namespaces['GoogleCertificate']['standaloneCourseEnrollmentModalCopyUpdates'];

export const generateStandaloneCourseModalCopyImpression = () => {
  return GoogleCertificateEpicClient.get('standaloneCourseEnrollmentModalCopyUpdates');
};

export const getStandaloneCourseModalCopyVariant = () => {
  return GoogleCertificateEpicClient.preview('standaloneCourseEnrollmentModalCopyUpdates');
};

export const getStandaloneCourseEnrollModalCopy = ({
  variant,
  courseName,
  price,
  secondaryCtaAction,
}: {
  variant: StandaloneCourseEnrollmentModalCopyUpdatesValues;
  courseName: string;
  price: React.ReactNode;
  secondaryCtaAction: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
}) => {
  const headerMessageCopy = {
    control: courseName,
    variant1: courseName,
    variant2: courseName,
    variant3: _t(`Choose how you’d like to start`),
  };

  const bodyMessageVariantCopy = (
    <FormattedMessage
      message={_t(
        `Get complete access to all course content, assignments, and a certificate by purchasing for {productPrice}, or preview the first module at no cost.`
      )}
      productPrice={<strong>{price}</strong>}
    />
  );
  const bodyMessageCopy = {
    control: (
      <FormattedMessage
        message={_t(
          `This course costs {productPrice}. Start your learning journey now and master the material in this course to earn a certificate that showcases your knowledge and achievement.`
        )}
        productPrice={price}
      />
    ),
    variant1: bodyMessageVariantCopy,
    variant2: bodyMessageVariantCopy,
    variant3: bodyMessageVariantCopy,
  };

  const primaryCtaCopy = {
    control: _t(`Continue`),
    variant1: _t(`Purchase full course`),
    variant2: _t(`Buy now`),
    variant3: _t(`Purchase full course`),
  };

  const secondaryCtaControl = (
    <FormattedMessage
      message={_t('{preview} this course')}
      preview={
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link component="button" onClick={secondaryCtaAction}>
          {_t('Preview')}
        </Link>
      }
    />
  );
  const secondaryCtaCopy = {
    control: secondaryCtaControl,
    variant1: secondaryCtaControl,
    variant2: secondaryCtaControl,
    variant3: (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link component="button" onClick={secondaryCtaAction}>
        {_t('Start free preview')}
      </Link>
    ),
  };

  return {
    headerMessage: headerMessageCopy[variant],
    bodyMessage: bodyMessageCopy[variant],
    primaryCta: primaryCtaCopy[variant],
    secondaryCta: secondaryCtaCopy[variant],
  };
};
